import React, { useEffect, useMemo, useState } from 'react';
import {
  PiMoonStarsFill,
  PiSpeakerSimpleHighBold,
  PiSpeakerSlashFill,
  PiSunDimFill,
} from 'react-icons/pi';

const FloatingButton = ({ screen, onSpeak, onZoomIn, onZoomOut, isSpeaking }) => {
  const getInitialThemeState = () => {
    if (typeof window === 'undefined') return 'dark';

    const savedTheme = localStorage.getItem('theme');
    const themeMode = localStorage.getItem('themeMode');

    if (themeMode === 'manual' && (savedTheme === 'light' || savedTheme === 'dark')) {
      return { theme: savedTheme, autoTheme: false };
    }

    return {
      theme: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
      autoTheme: true,
    };
  };

  const initialThemeState = useMemo(() => getInitialThemeState(), []);
  const [theme, setTheme] = useState(initialThemeState.theme);
  const [autoTheme, setAutoTheme] = useState(initialThemeState.autoTheme);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (!autoTheme) return undefined;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleThemeChange = (event) => {
      if (localStorage.getItem('themeMode') === 'manual') return;
      setTheme(event.matches ? 'dark' : 'light');
    };

    localStorage.setItem('themeMode', 'auto');
    mediaQuery.addEventListener('change', handleThemeChange);
    return () => mediaQuery.removeEventListener('change', handleThemeChange);
  }, [autoTheme]);

  const handleThemeToggle = () => {
    setTheme((prevTheme) => {
      const nextTheme = prevTheme === 'dark' ? 'light' : 'dark';
      localStorage.setItem('themeMode', 'manual');
      localStorage.setItem('theme', nextTheme);
      setAutoTheme(false);
      setAnnouncement(nextTheme === 'light' ? 'Light theme enabled.' : 'Dark theme enabled.');
      return nextTheme;
    });
  };

  const handleAudioToggle = () => {
    try {
      localStorage.setItem('rnibAudioEnabled', String(!isSpeaking));
    } catch {
      // Ignore storage failures and still trigger the page-level handler.
    }

    onSpeak?.();
  };

  return (
    <>
      <aside className="fixed bottom-4 right-3 z-[1200] flex flex-col items-center gap-3 sm:right-6 sm:top-1/3 sm:bottom-auto">
        <span className="sr-only" aria-live="polite">
          {announcement}
        </span>

        <div className="flex flex-col items-center gap-3 rounded-[1.5rem] border border-glass-border bg-panel/90 p-2.5 shadow-lg backdrop-blur sm:rounded-[1.75rem] sm:p-3">
          <button
            type="button"
            className="h-14 w-14 rounded-full border border-glass-border bg-accent text-[color:var(--accent-foreground)] shadow-lg transition hover:bg-teal focus:outline-none focus:ring-4 focus:ring-yellow-accent sm:h-16 sm:w-16"
            onClick={handleAudioToggle}
            aria-label={isSpeaking ? 'Stop reading page content' : 'Read page content aloud'}
            title={isSpeaking ? 'Stop reading' : 'Read content'}
          >
            {isSpeaking ? (
              <PiSpeakerSlashFill className="mx-auto text-2xl" />
            ) : (
              <PiSpeakerSimpleHighBold className="mx-auto text-2xl" />
            )}
          </button>

          {screen === 'dashboard' && (
            <div className="flex flex-col items-center gap-3">
              <button
                type="button"
                className="h-12 w-12 rounded-full border border-glass-border bg-bg-blue text-lg text-text-light shadow-lg transition hover:bg-teal focus:outline-none focus:ring-4 focus:ring-yellow-accent sm:h-14 sm:w-14 sm:text-xl"
                onClick={onZoomIn}
                aria-label="Increase zoom"
                title="Zoom in"
              >
                +
              </button>
              <button
                type="button"
                className="h-12 w-12 rounded-full border border-glass-border bg-bg-blue text-lg text-text-light shadow-lg transition hover:bg-teal focus:outline-none focus:ring-4 focus:ring-yellow-accent sm:h-14 sm:w-14 sm:text-xl"
                onClick={onZoomOut}
                aria-label="Decrease zoom"
                title="Zoom out"
              >
                -
              </button>
            </div>
          )}
        </div>

        <div className="rounded-[1.5rem] border border-glass-border bg-panel/90 p-2.5 shadow-lg backdrop-blur sm:rounded-[1.75rem] sm:p-3 flex">
          <button
            type="button"
            className="h-12 w-12 rounded-full border border-glass-border bg-panel text-text-light shadow-lg transition hover:bg-muted focus:outline-none focus:ring-4 focus:ring-yellow-accent sm:h-14 sm:w-14"
            onClick={handleThemeToggle}
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            title={theme === 'dark' ? 'Light theme' : 'Dark theme'}
          >
            {theme === 'dark' ? (
              <PiSunDimFill className="mx-auto text-2xl" />
            ) : (
              <PiMoonStarsFill className="mx-auto text-2xl" />
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

export default FloatingButton;
